import { useEffect, useRef, useState } from "react";
import "./PresentationVideo.css";

export const PresentationVideo = () => {
  const ref = useRef(null);
  const allowed = useRef(false);
  const userPaused = useRef(false);
  const audibleTried = useRef(false);
  const epoch = useRef(0);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const video = ref.current;
    const step = video.closest(".seq-step");
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;
    let pending = false;
    let disposed = false;
    const sync = async () => {
      const style = getComputedStyle(step || video);
      allowed.current = visible && !document.hidden && style.visibility !== "hidden" && Number(style.opacity) > 0.05;
      if (!allowed.current) {
        epoch.current += 1;
        if (!video.paused) video.pause();
        return;
      }
      if (motion.matches || userPaused.current || pending || !video.paused || video.ended) return;
      pending = true;
      const attempt = ++epoch.current;
      // Sound only where the browser's autoplay policy allows it; no workaround.
      if (!audibleTried.current) {
        audibleTried.current = true;
        video.muted = false;
      }
      try {
        await video.play();
      } catch {
        if (!disposed && allowed.current && attempt === epoch.current) {
          video.muted = true;
          try { await video.play(); } catch { /* the play button stays available */ }
        }
      } finally {
        pending = false;
        if (disposed || !allowed.current || userPaused.current) video.pause();
      }
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); }, { threshold: 0.15 });
    io.observe(video);
    // Desktop GSAP hides the BUILD step in place (autoAlpha) without leaving the viewport.
    const mo = new MutationObserver(sync);
    if (step) mo.observe(step, { attributes: true, attributeFilter: ["style", "class"] });
    document.addEventListener("visibilitychange", sync);
    const onMotion = () => (motion.matches ? video.pause() : sync());
    motion.addEventListener("change", onMotion);
    return () => {
      disposed = true;
      epoch.current += 1;
      video.pause();
      io.disconnect();
      mo.disconnect();
      document.removeEventListener("visibilitychange", sync);
      motion.removeEventListener("change", onMotion);
    };
  }, []);

  const playFromStart = async (sound) => {
    const video = ref.current;
    epoch.current += 1;
    userPaused.current = false;
    if (sound) {
      video.muted = false;
      video.currentTime = 0;
    }
    try { await video.play(); setPlaying(!video.paused); } catch { setPlaying(false); }
  };
  const togglePlay = () => {
    if (playing) {
      userPaused.current = true;
      epoch.current += 1;
      ref.current.pause();
    } else playFromStart(false);
  };

  return (
    <div className="presentation-video" data-testid="presentation-video">
      <video ref={ref} width="720" height="1280" playsInline preload="metadata"
        poster="/media/novasoft-presentation.webp" aria-label="Vidéo de présentation de NovaSoft"
        data-testid="presentation-video-player"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => { userPaused.current = true; setPlaying(false); }}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)} onError={() => setError(true)}>
        <source src="/media/novasoft-presentation.webm" type="video/webm" />
        <source src="/media/novasoft-presentation.mp4" type="video/mp4" onError={() => setError(true)} />
      </video>
      <div className="presentation-video-controls">
        <button type="button" onClick={togglePlay} data-testid="presentation-video-toggle">
          {playing ? "Mettre en pause" : "Lire la vidéo"}
        </button>
        {playing && (muted ? (
          <button type="button" onClick={() => playFromStart(true)} aria-label="Activer le son et reprendre la vidéo au début"
            data-testid="presentation-video-sound">Activer le son</button>
        ) : (
          <button type="button" onClick={() => { ref.current.muted = true; }} data-testid="presentation-video-mute">Couper le son</button>
        ))}
      </div>
      {error && <p role="status" data-testid="presentation-video-error">La vidéo est indisponible pour le moment.</p>}
    </div>
  );
};
